import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import {
  type DeployPhase as DeployPhaseState,
  type DeployProbe,
  deployMessage,
  deployPhase,
  deployProgress,
} from '@/lib/deployWatch';

type CheckResult = {
  current: { version: string; commit: string; build_date: string | null };
  latest: {
    version: string;
    name?: string | null;
    notes?: string | null;
    published_at?: string | null;
    url?: string | null;
  } | null;
  status: 'atualizado' | 'atualizacao_disponivel' | 'nao_configurado' | 'desenvolvimento' | 'erro';
  message: string;
  update_available: boolean;
  deploy_configured: boolean;
  /** Quantos serviços o botão alcança. São dois: aplicação e agendador. */
  deploy_targets: number;
};

/**
 * Estado do ESQUEMA do banco, que é independente do da versão.
 *
 * O botão Atualizar troca o código; o banco fica como está. Um sistema com
 * código novo e esquema velho falha em telas que funcionavam minutos antes,
 * e nada liga o sintoma à atualização — daí este bloco existir na mesma tela.
 */
type DatabaseState = {
  pending: string[];
  count: number;
  applied: number;
  up_to_date: boolean;
  recent_backup: { file: string; created_at: string } | null;
  note: string;
};

/**
 * Versão e atualização — RF-ADM.
 *
 * ================================================================
 * O QUE ESTE BOTÃO FAZ, E O QUE ELE NÃO FAZ
 * ================================================================
 *
 * Ele NÃO baixa código do Git para dentro do servidor. A aplicação roda a
 * partir de uma imagem imutável — o código está dentro dela, e um `git pull`
 * lá dentro alteraria arquivos que o próximo restart descarta.
 *
 * O que acontece: o botão avisa o Portainer, que baixa a imagem já construída
 * pelo GitHub Actions e reimplanta a stack.
 *
 * A tela diz isso em texto porque a expectativa natural é a outra, e um botão
 * cujo efeito o usuário entende errado é pior que um botão ausente.
 */
export function UpdatesPage() {
  /**
   * A atualização em andamento.
   *
   * `target` é a versão que se espera ver no fim, e `startedAt` de quando
   * começou. Guardar os dois em estado — e não num objeto de mutation — é o que
   * permite o acompanhamento sobreviver ao servidor cair: nenhuma consulta que
   * falha derruba o que já se sabe.
   */
  const [watching, setWatching] = useState<{ target: string; startedAt: number } | null>(null);
  const [probes, setProbes] = useState<DeployProbe[]>([]);

  const [deployNote, setDeployNote] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [withBackup, setWithBackup] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  /**
   * ================================================================
   * CONSULTA DIRETA, FORA DO TanStack Query
   * ================================================================
   *
   * O cliente de consultas trata erro de rede como falha e para de tentar — que
   * é o comportamento certo em todo o resto do sistema e o errado aqui: durante
   * a troca de contêiner, falhar é o esperado, e é justamente o sinal de que
   * está funcionando.
   *
   * Este laço trata cada erro como "ainda subindo" e só encerra quando a versão
   * nova responde, ou quando o tempo acaba.
   */
  const abortRef = useRef(false);

  useEffect(() => {
    if (!watching) return;

    abortRef.current = false;

    const timer = setInterval(async () => {
      if (abortRef.current) return;

      try {
        const r = await api.get<{ data: { version: string } }>('/system/version');

        setProbes((p) => [...p, { kind: 'version', version: r.data.version }]);
      } catch {
        // Sem tratamento por tipo de erro: qualquer falha aqui significa a
        // mesma coisa — o servidor ainda não está de pé.
        setProbes((p) => [...p, { kind: 'unreachable' }]);
      }
    }, 3_000);

    return () => {
      abortRef.current = true;
      clearInterval(timer);
    };
  }, [watching]);

  const elapsed = watching ? Date.now() - watching.startedAt : 0;
  const phase = watching ? deployPhase(watching.target, probes, elapsed) : null;

  // Terminou: recarrega os dados da tela, mas NÃO fecha o aviso — quem clicou
  // precisa ler que terminou, e não descobrir pelo silêncio.
  useEffect(() => {
    if (phase?.phase === 'concluido') {
      abortRef.current = true;
      void queryClient.invalidateQueries({ queryKey: ['system', 'check-update'] });
    }
  }, [phase?.phase, queryClient]);

  const check = useQuery({
    queryKey: ['system', 'check-update'],
    queryFn: () =>
      api.get<{ data: CheckResult; meta: { database: DatabaseState } }>('/system/check-update'),
  });

  const recheck = useMutation({
    mutationFn: () =>
      api.get<{ data: CheckResult; meta: { database: DatabaseState } }>(
        '/system/check-update?force=1',
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['system'] }),
  });

  /**
   * ================================================================
   * CONSERTO DE PASSAGEM — REMOVER DEPOIS
   * ================================================================
   *
   * "Faça um comando de correção, porém coloque um botão separado em
   * Atualizações para eu não precisar entrar no Portainer. Depois removemos
   * esse botão."
   *
   * Até a v0.49.6, o ajuste de uma baixa com desconto num título de RECEITA
   * nascia como receita em vez de despesa. Cada um desses infla a conta em duas
   * vezes o valor — uma por não subtrair, outra por somar.
   *
   * O código já nasce certo. O que está gravado não se conserta sozinho, e não
   * dá pela tela: o tipo de um lançamento não é editável por desenho.
   *
   * ESTE BLOCO INTEIRO SAI quando as bases estiverem corrigidas — junto com a
   * rota, o método do controller e `AdjustmentSignRepair`. Botão que troca
   * receita por despesa não pode virar parte permanente do sistema.
   *
   * Só aparece quando há algo a consertar: base limpa não mostra nada.
   */
  const ajustes = useQuery({
    queryKey: ['system', 'ajustes-invertidos'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: number;
          description: string;
          amount_formatado: string;
          type_atual: string;
          type_correto: string;
          impacto_no_saldo: string;
        }>;
        meta: { count: number; note: string };
      }>('/system/ajustes-invertidos'),
    staleTime: 60_000,
  });

  const corrigirAjustes = useMutation({
    mutationFn: () =>
      api.post<{ meta: { note: string } }>('/system/ajustes-invertidos', {
        confirmation: 'CORRIGIR',
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['system'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['statement'] });
      alert(res.meta.note);
    },
    onError: (e) =>
      alert(e instanceof ApiError ? e.message : 'Não foi possível aplicar a correção.'),
  });

  /**
   * Aplicar migrações.
   *
   * Fica nesta tela e não na de Backup porque é aqui que a necessidade
   * aparece: quem acabou de atualizar é quem precisa migrar.
   */
  const migrate = useMutation({
    mutationFn: () =>
      api.post<{ data: { ran: string[] }; meta: { note: string } }>('/system/migrate', {
        confirmation: 'MIGRAR',
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['system'] });
      alert(res.meta.note);
    },
    onError: (e) =>
      alert(e instanceof ApiError ? e.message : 'Não foi possível aplicar as migrações.'),
  });

  const update = useMutation({
    mutationFn: () =>
      api.post<{ data: { note: string; backup: { file: string } | null } }>('/system/update', {
        confirmation,
        backup: withBackup,
      }),
    onSuccess: (res) => {
      setConfirmOpen(false);
      setConfirmation('');

      /**
       * Sem `alert`: ele exigia um clique para sumir e depois deixava a pessoa
       * sem saber quando a atualização terminou — que era exatamente a queixa.
       *
       * A nota do servidor entra no painel de acompanhamento, junto do
       * progresso, e fica visível até o fim.
       */
      setProbes([]);
      setWatching({
        target: check.data?.data.latest?.version ?? '',
        startedAt: Date.now(),
      });
      setDeployNote(res.data.note);
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        setError(Object.values(e.errors ?? {}).flat()[0] ?? e.message);
        return;
      }
      setError('Não foi possível iniciar a atualização.');
    },
  });

  const deployPanel = watching && phase && (
    <DeployProgress
      state={phase}
      target={watching.target}
      note={deployNote}
      onClose={() => {
        setWatching(null);
        setProbes([]);
        setDeployNote(null);
      }}
    />
  );

  const d = check.data?.data;
  const db = check.data?.meta.database;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-xl font-semibold tracking-tight">Atualizações</h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Verifica se há uma versão nova publicada e pede ao Portainer que reimplante o sistema.
      </p>

      {check.isPending ? (
        <div className="mt-5 h-32 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
      ) : check.isError ? (
        <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          {check.error instanceof ApiError && check.error.status === 403
            ? 'Você não tem permissão para verificar atualizações.'
            : 'Não foi possível consultar.'}
        </p>
      ) : d ? (
        <>
          {/* ---------- Versão em execução ---------- */}
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Versão em execução
            </p>
            <p className="mt-1 text-2xl font-semibold">{d.current.version}</p>
            <p className="mt-0.5 text-xs text-slate-400">
              commit {d.current.commit}
              {d.current.build_date &&
                ` · publicada em ${new Date(d.current.build_date).toLocaleString('pt-BR')}`}
            </p>
          </div>

          {/* ---------- Estado do banco ---------- */}
          {/*
            O QUE O BOTÃO ATUALIZAR NÃO FAZ.

            Ele troca a imagem em execução — o código. O banco não é tocado: o
            container sobe com `RUN_MIGRATIONS=false`, porque migração é
            alteração de esquema e não pode acontecer a cada restart do Swarm,
            que pode ocorrer de madrugada por um healthcheck instável, sem
            ninguém por perto e sem backup recente.

            Numa versão que traga migração, atualizar sem migrar deixa código
            novo sobre esquema velho. O sintoma é "Erro interno" em telas que
            funcionavam cinco minutos antes, e nada liga isso à atualização.
            Por isso o aviso vem ANTES do botão, e não como erro depois.
          */}
          {db && !db.up_to_date && (
            <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {db.count} alteração(ões) de banco pendente(s)
              </p>
              <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/90">{db.note}</p>

              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-amber-800 dark:text-amber-300">
                  ver quais
                </summary>
                <ul className="mt-1 list-inside list-disc text-xs text-amber-800/80 dark:text-amber-300/80">
                  {db.pending.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </details>

              {/*
                Backup recente é pré-requisito, e a tela diz isso antes do
                clique. Migração pode ser irreversível — um `drop column` não
                tem desfazer —, e o caminho de volta é restaurar.
              */}
              {db.recent_backup ? (
                <>
                  <p className="mt-2.5 text-xs text-amber-800/90 dark:text-amber-300/90">
                    Backup de rede: <strong>{db.recent_backup.file}</strong>
                  </p>

                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Aplicar ${db.count} alteração(ões) no banco?\n\n` +
                            'Migração muda a estrutura do banco e pode ser irreversível — o ' +
                            'caminho de volta é restaurar o backup.\n\n' +
                            `Rede de segurança: ${db.recent_backup?.file}\n\n` +
                            'Continuar?',
                        )
                      ) {
                        migrate.mutate();
                      }
                    }}
                    disabled={migrate.isPending}
                    className="mt-2 rounded-lg bg-amber-700 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-amber-800 disabled:opacity-50"
                  >
                    {migrate.isPending ? 'Aplicando…' : 'Aplicar alterações no banco'}
                  </button>
                </>
              ) : (
                <p className="mt-2.5 rounded-lg bg-white/70 px-3 py-2 text-xs text-amber-900 dark:bg-slate-900/60 dark:text-amber-200">
                  <strong>Gere um backup antes.</strong> Vá em <strong>Backup</strong>, clique em
                  "Gerar backup agora" e volte aqui — o botão de aplicar aparece quando houver um
                  backup das últimas 24 horas.
                </p>
              )}
            </div>
          )}

          {/* ================================================================
              CONSERTO DE PASSAGEM — REMOVER JUNTO COM A ROTA
              ================================================================

              Cor diferente da migração de propósito: são coisas diferentes.
              Migração é rotina de toda atualização; isto é um conserto único de
              dados que já nasceram errados, e some quando não houver mais o que
              consertar. */}
          {(ajustes.data?.meta.count ?? 0) > 0 && (
            <div className="mt-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 dark:border-rose-900/60 dark:bg-rose-950/30">
              <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
                Correção pontual: {ajustes.data?.meta.count} ajuste(s) de baixa com receita e
                despesa trocados
              </p>

              <p className="mt-1 text-xs text-rose-800/90 dark:text-rose-300/90">
                Defeito corrigido nesta versão — novos lançamentos já nascem certos. Estes ficaram
                gravados errados e inflam o saldo da conta em duas vezes o valor. O tipo de um
                lançamento não é editável pela tela, por isso este botão.
              </p>

              <ul className="mt-2 space-y-1">
                {ajustes.data?.data.map((a) => (
                  <li
                    key={a.id}
                    className="rounded-lg bg-white/70 px-3 py-2 text-xs dark:bg-slate-900/50"
                  >
                    <span className="font-medium">{a.description}</span>
                    <span className="block text-slate-500 dark:text-slate-400">
                      {a.amount_formatado} · está como {a.type_atual}, deveria ser {a.type_correto} ·
                      efeito no saldo: <strong>{a.impacto_no_saldo}</strong>
                    </span>
                  </li>
                ))}
              </ul>

              {/* Mesma exigência da migração, e pelo mesmo motivo: isto mexe em
                  saldo de conta, e o caminho de volta é restaurar. */}
              {db?.recent_backup ? (
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Corrigir ${ajustes.data?.meta.count} ajuste(s)?\n\n` +
                          'Os lançamentos passam de receita para despesa e o saldo das contas ' +
                          'afetadas muda.\n\n' +
                          `Rede de segurança: ${db.recent_backup?.file}\n\n` +
                          'Continuar?',
                      )
                    ) {
                      corrigirAjustes.mutate();
                    }
                  }}
                  disabled={corrigirAjustes.isPending}
                  className="mt-2.5 rounded-lg bg-rose-700 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-rose-800 disabled:opacity-50"
                >
                  {corrigirAjustes.isPending ? 'Corrigindo…' : 'Corrigir os ajustes'}
                </button>
              ) : (
                <p className="mt-2.5 rounded-lg bg-white/70 px-3 py-2 text-xs text-rose-900 dark:bg-slate-900/60 dark:text-rose-200">
                  <strong>Gere um backup antes.</strong> Vá em <strong>Backup</strong>, clique em
                  "Gerar backup agora" e volte aqui.
                </p>
              )}
            </div>
          )}

          {/* Banco em dia também precisa ser dito: silêncio aqui deixaria a
              dúvida sobre se a checagem chegou a acontecer. */}
          {db && db.up_to_date && (
            <p className="mt-3 text-xs text-slate-400">
              Banco em dia — {db.applied} alterações aplicadas, nenhuma pendente.
            </p>
          )}

          {/* ---------- Situação ---------- */}
          <div
            className={`mt-3 rounded-xl border p-4 ${
              d.update_available
                ? 'border-indigo-300 bg-indigo-50 dark:border-indigo-900/50 dark:bg-indigo-950/30'
                : d.status === 'atualizado'
                  ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30'
                  : 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'
            }`}
          >
            <p className="text-sm font-medium">{d.message}</p>

            {d.latest?.notes && d.update_available && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  O que mudou
                </p>
                <pre className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-white/70 p-3 text-xs dark:bg-slate-900/60">
                  {d.latest.notes}
                </pre>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => recheck.mutate()}
                disabled={recheck.isPending}
                className="rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm transition hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
              >
                {recheck.isPending ? 'Verificando…' : 'Verificar novamente'}
              </button>

              {/*
                O botao SOME enquanto a atualizacao acontece.

                Deixa-lo clicavel convidaria a disparar de novo achando que "nao
                pegou" — e um segundo webhook no meio de uma reimplantacao e a
                receita para o Portainer se atrapalhar com duas trocas
                simultaneas.
              */}
              {d.update_available && d.deploy_configured && watching === null && (
                <button
                  onClick={() => {
                    setError(null);
                    setConfirmOpen(true);
                  }}
                  className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
                >
                  Atualizar para {d.latest?.version}
                </button>
              )}
            </div>

            {/* Um webhook só cobre a aplicação e esquece o agendador — que
                some da vista rodando a versão antiga, com o backup e os
                alertas junto. Vale avisar antes de o usuário clicar. */}
            {d.deploy_configured && d.deploy_targets < 2 && (
              <p className="mt-3 text-xs text-amber-800 dark:text-amber-300">
                Apenas <strong>1 serviço</strong> configurado. São dois usando a mesma imagem — a
                aplicação e o <strong>agendador</strong>, que roda backup, faturamento de contratos
                e alertas. Sem o segundo webhook, ele continuaria na versão antiga sem aviso.
              </p>
            )}

            {d.update_available && !d.deploy_configured && (
              /* Botão ausente sem explicação parece defeito. */
              <p className="mt-3 text-xs text-amber-800 dark:text-amber-300">
                Há versão nova, mas a atualização automática não está configurada. No Portainer,
                abra <strong>Services → financeiro_financeiro</strong>, ative o{' '}
                <strong>Service webhook</strong> e copie a URL; repita em{' '}
                <strong>financeiro_financeiro_scheduler</strong>. Defina as duas em{' '}
                <code>PORTAINER_DEPLOY_WEBHOOK</code>, separadas por vírgula. Enquanto isso,
                atualize pelo Portainer com <strong>Update the stack → Re-pull image</strong>.
              </p>
            )}
          </div>

          {/* ---------- Como funciona ---------- */}
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            <p className="font-semibold uppercase tracking-wider text-slate-400">Como funciona</p>
            <ol className="mt-2 list-decimal space-y-1 pl-4">
              <li>Você publica uma versão no repositório com uma etiqueta (ex.: <code>v0.19.0</code>)</li>
              <li>O GitHub Actions constrói a imagem e cria a release</li>
              <li>Este botão avisa o Portainer, que baixa a imagem e reimplanta</li>
            </ol>
            <p className="mt-2">
              O sistema <strong>não</strong> baixa código para dentro do servidor — ele roda a partir
              de uma imagem pronta. Por isso a atualização passa pelo Portainer, e não por um{' '}
              <code>git pull</code>.
            </p>
          </div>
        </>
      ) : null}

      {/* ---------- Confirmação ---------- */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm dark:bg-black/70">
          <div className="mt-16 w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-lg font-semibold">Atualizar o sistema</h2>

            <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <p>
                O sistema vai de <strong>{d?.current.version}</strong> para{' '}
                <strong>{d?.latest?.version}</strong>.
              </p>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                O sistema fica fora do ar por alguns segundos ao final. Se houver migração de banco,
                ela é aplicada — por isso o backup vem marcado.
              </p>
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={withBackup}
                onChange={(e) => setWithBackup(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
              />
              <span>
                Gerar backup verificado antes
                <span className="block text-xs text-slate-400">
                  Se o backup falhar, a atualização é cancelada. Desmarcar só faz sentido se você
                  acabou de gerar um.
                </span>
              </span>
            </label>

            <div className="mt-3">
              <label className="block text-sm font-medium">
                Digite <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">ATUALIZAR</code>{' '}
                para confirmar
              </label>
              <input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoFocus
                className="input mt-1 w-full"
              />
            </div>

            {error && (
              <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
                {error}
              </p>
            )}

            {/* O acompanhamento fica logo acima dos botões: é onde o olho
                estava quando clicou. */}
            {deployPanel}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmation('');
                  setError(null);
                }}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setError(null);
                  update.mutate();
                }}
                disabled={update.isPending || confirmation !== 'ATUALIZAR'}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
              >
                {update.isPending ? 'Disparando…' : 'Atualizar agora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// -------------------------------------------------------------------

/**
 * O acompanhamento da atualização — RF-ADM-07.
 *
 * ================================================================
 * POR QUE A BARRA NÃO ANDA SOZINHA
 * ================================================================
 *
 * Ninguém sabe quanto falta: o Portainer não informa, e o tempo depende do
 * tamanho da imagem e da rede do servidor. Uma barra que sobe com o relógio
 * até 90% e trava ali é mentira — a pessoa lê "quase pronto" e espera parado.
 *
 * Esta anda por FASE ALCANÇADA, e cada fase é um fato verificado: o servidor
 * parou de responder, voltou, voltou com a versão nova. Ela pode ficar parada
 * por um minuto, e isso é honesto — nada de novo aconteceu.
 */
function DeployProgress({
  state,
  target,
  note,
  onClose,
}: {
  state: DeployPhaseState;
  target: string;
  note: string | null;
  onClose: () => void;
}) {
  const pronto = state.phase === 'concluido';
  const demorou = state.phase === 'demorando';

  return (
    <div
      className={`mt-4 rounded-xl border px-4 py-3 ${
        pronto
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25'
          : demorou
            ? 'border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/25'
            : 'border-indigo-200 bg-indigo-50 dark:border-indigo-900/50 dark:bg-indigo-950/25'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">
          {pronto ? 'Atualização concluída' : demorou ? 'Ainda sem confirmação' : 'Atualizando…'}
        </span>

        {!pronto && !demorou && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            a página vai avisar quando terminar — pode deixar aberta
          </span>
        )}

        {(pronto || demorou) && (
          <button
            onClick={onClose}
            className="ml-auto rounded-lg border border-slate-300 px-2.5 py-1 text-xs transition hover:bg-white dark:border-slate-700 dark:hover:bg-slate-900"
          >
            Fechar
          </button>
        )}
      </div>

      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/70 dark:bg-slate-900/60">
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            pronto ? 'bg-emerald-500' : demorou ? 'bg-amber-500' : 'bg-indigo-500'
          }`}
          style={{ width: `${Math.round(deployProgress(state) * 100)}%` }}
        />
      </div>

      <p className="mt-2 text-sm">{deployMessage(state, target)}</p>

      {/* A nota do servidor — inclui o backup gerado, se houve. */}
      {note && !pronto && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{note}</p>
      )}

      {pronto && (
        <p className="mt-1 text-xs text-emerald-800/80 dark:text-emerald-300/80">
          Recarregue a página para carregar a interface nova.
        </p>
      )}
    </div>
  );
}
